import Checkbox from '@atlaskit/checkbox';
import { Box, xcss } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import { type EditComponentProps } from '../edit/types';

import CheckboxValueSelect from './CheckboxValueSelect';
import { type ViewComponentProps } from './types';

type CheckboxValueViewProps = ViewComponentProps<DatabaseFieldType.CHECKBOX> & {
  // Provided when rendered as the `edit` component (bulk-edit, filter, default value).
  onChange?: EditComponentProps<DatabaseFieldType.CHECKBOX>['onChange'];
  isBulkEdit?: EditComponentProps<DatabaseFieldType.CHECKBOX>['isBulkEdit'];
  isDefaultValue?: EditComponentProps<DatabaseFieldType.CHECKBOX>['isDefaultValue'];
  isFilterValueEdit?: EditComponentProps<DatabaseFieldType.CHECKBOX>['isFilterValueEdit'];
  ariaLabel?: EditComponentProps<DatabaseFieldType.CHECKBOX>['ariaLabel'];
};

const checkboxWrapperStyles = xcss({
  display: 'inline-flex'
});

const checkboxMarginStyles = xcss({
  marginInlineStart: 'space.negative.050'
});

const CheckboxValueView: FunctionComponent<CheckboxValueViewProps> = ({
  field,
  contentId,
  value,
  entryId,
  onChange,
  isReadonly,
  isBulkEdit,
  isDefaultValue,
  isFilterValueEdit,
  ariaLabel,
  isEmbedValueView
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { formatMessage } = useIntl();
  const isCheckboxEnabled =
    !isReadonly &&
    getExperimentValue('checkboxFieldType', 'enabled', {
      fireExperimentExposure: false
    });
  const accessibleValueText = formatMessage(
    value ? messages['checkbox-filter.checked'] : messages['checkbox-filter.unchecked']
  );

  const handleChange = useCallback(
    (nextValue: boolean) => {
      if (onChange) {
        onChange(nextValue, {
          type: DatabaseFieldType.CHECKBOX,
          value: nextValue
        });
        return;
      }

      if (!entryId) {
        return;
      }

      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_VALUE,
          fieldId: field.id,
          entryId,
          value: {
            type: DatabaseFieldType.CHECKBOX,
            value: nextValue
          },
          change: {
            type: DatabaseFieldType.CHECKBOX,
            value: nextValue
          }
        })
      );
    },
    [contentId, dispatch, entryId, field.id, onChange]
  );

  const toggleCheckbox = useCallback(() => {
    handleChange(!value);
  }, [handleChange, value]);

  if (isDefaultValue || isFilterValueEdit) {
    return (
      <CheckboxValueSelect ariaLabel={ariaLabel} onChange={handleChange} value={value} />
    );
  }

  return (
    <Box
      xcss={[
        checkboxWrapperStyles,
        !isBulkEdit && !isEmbedValueView && checkboxMarginStyles
      ]}
      data-databases-checkbox-value=""
    >
      <VisuallyHidden>{accessibleValueText}</VisuallyHidden>
      <Checkbox
        testId="databases-checkbox"
        isChecked={value}
        onChange={toggleCheckbox}
        isDisabled={!isCheckboxEnabled}
        size="medium"
        style={{ cursor: 'pointer' }}
      />
    </Box>
  );
};

export default CheckboxValueView;
